import React, { useState } from 'react';
import { Bell, Search, LogOut, Settings, ChevronDown, AlertTriangle, UserPlus, MessageSquare, Sun, Moon } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useNotificationStore, type AppNotification } from '@/features/notifications/notificationStore';
import { useTheme } from '@/providers/ThemeProvider';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const NOTIF_ICON: Record<AppNotification['type'], React.ReactNode> = {
  escalation: <AlertTriangle className="h-4 w-4 text-error" />,
  lead: <UserPlus className="h-4 w-4 text-primary" />,
  message: <MessageSquare className="h-4 w-4 text-slate-400" />,
};

const ROUTE_INFO: Record<string, { title: string; description: string }> = {
  '/dashboard': {
    title: 'Dashboard',
    description: 'Welcome back! Live overview of your store automation.',
  },
  '/conversations': {
    title: 'Conversations',
    description: 'Live unified inbox across all channels and storefronts.',
  },
  '/leads': {
    title: 'Leads',
    description: 'Track and manage customer leads captured across channels.',
  },
  '/knowledge': {
    title: 'Knowledge Base',
    description: 'AI product catalog and business policy documents.',
  },
  '/automations': {
    title: 'Automations',
    description: 'AI auto-reply triggers, tone, and keyword rules.',
  },
  '/integrations': {
    title: 'Integrations',
    description: 'Connected storefronts and messaging platforms.',
  },
  '/analytics': {
    title: 'Analytics',
    description: 'Deep-dive conversation performance & CSAT metrics.',
  },
  '/settings': {
    title: 'Settings',
    description: 'Account, workspace identity, and team preferences.',
  },
};

function getInitials(first?: string | null, last?: string | null, email?: string | null) {
  const f = first?.trim()?.[0];
  const l = last?.trim()?.[0];
  if (f || l) return `${f ?? ''}${l ?? ''}`.toUpperCase();
  return (email?.trim()?.[0] ?? 'U').toUpperCase();
}

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { notifications, unreadCount, markAllRead, clear } = useNotificationStore();

  const currentRouteMeta = ROUTE_INFO[location.pathname] ?? {
    title: 'Omnibiz',
    description: 'AI Customer Service Platform',
  };

  const toggleNotifications = () => {
    setShowNotifications((v) => {
      if (!v) markAllRead();
      return !v;
    });
  };

  const openNotification = (n: AppNotification) => {
    setShowNotifications(false);
    if (n.conversationId) navigate('/conversations');
  };

  const initials = getInitials(user?.firstName, user?.lastName, user?.email);
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Your account';

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <header className="relative z-50 h-16 shrink-0 bg-white/80 dark:bg-[#0B1120]/80 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800/80 px-4 sm:px-6 flex items-center justify-between gap-4 transition-colors">
      {/* Left: Dynamic Page Title & Description */}
      <div className="flex flex-col min-w-0 pr-2">
        <h1 className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 leading-tight truncate">
          {currentRouteMeta.title}
        </h1>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate hidden lg:block leading-tight mt-0.5">
          {currentRouteMeta.description}
        </p>
      </div>

      {/* Right Controls Group: Search Bar, Theme Toggle, Notifications, Divider, User Menu */}
      <div className="flex items-center gap-2.5 sm:gap-3 ml-auto">
        {/* Right-aligned Pill Search Bar */}
        <div className="relative w-44 sm:w-56 md:w-64 group">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 h-3.5 w-3.5 pointer-events-none transition-colors group-focus-within:text-primary" />
          <input
            type="text"
            placeholder="Search anything..."
            className="pl-9 pr-4 h-9 w-full rounded-full border border-slate-200/90 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-900/90 text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 focus:bg-white dark:focus:bg-slate-850 transition-all shadow-2xs"
          />
        </div>

        {/* Animated Dark Mode Toggle */}
        <Tooltip content={resolvedTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'} placement="bottom">
          <button
            type="button"
            onClick={toggleTheme}
            className="relative h-9 w-9 grid place-items-center rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-transparent hover:border-slate-200 dark:hover:border-slate-700/60 transition-all active:scale-95"
            aria-label="Toggle theme"
          >
            {resolvedTheme === 'dark' ? (
              <Sun className="h-4 w-4 text-amber-400 animate-in spin-in-90 duration-300" />
            ) : (
              <Moon className="h-4 w-4 text-slate-600 dark:text-slate-300 animate-in spin-in-90 duration-300" />
            )}
          </button>
        </Tooltip>

        {/* Notifications */}
        <div className="relative">
          <Tooltip content="Notifications" placement="bottom">
            <button
              type="button"
              onClick={toggleNotifications}
              className={cn(
                'relative h-9 w-9 grid place-items-center rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-transparent hover:border-slate-200 dark:hover:border-slate-700/60 transition-all active:scale-95',
                showNotifications && 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700/80'
              )}
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-error px-1 text-[10px] font-semibold text-white ring-2 ring-white dark:ring-[#0B1120]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </Tooltip>

          {showNotifications && (
            <div className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-3 py-2.5">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Notifications</span>
                {notifications.length > 0 && (
                  <button
                    type="button"
                    onClick={clear}
                    className="text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="px-3 py-8 text-center text-sm text-slate-400 dark:text-slate-500">You're all caught up 🎉</p>
                ) : (
                  notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => openNotification(n)}
                      className="flex w-full items-start gap-2.5 border-b border-slate-50 dark:border-slate-800/60 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    >
                      <span className="mt-0.5 shrink-0">{NOTIF_ICON[n.type]}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">{n.title}</span>
                        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{n.body}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />

        {/* User menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowUserMenu((v) => !v)}
            className={cn(
              'flex items-center gap-2 rounded-lg pl-1.5 pr-2 h-9 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors',
              showUserMenu && 'bg-slate-100 dark:bg-slate-800'
            )}
          >
            <span className="h-7 w-7 grid place-items-center rounded-full bg-primary text-white text-xs font-semibold">
              {initials}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-slate-400 transition-transform',
                showUserMenu && 'rotate-180'
              )}
            />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-12 w-64 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2 shadow-lg z-50">
              <div className="flex items-center gap-3 px-2 py-2.5 border-b border-slate-100 dark:border-slate-800">
                <span className="h-9 w-9 grid place-items-center rounded-full bg-primary text-white text-sm font-semibold shrink-0">
                  {initials}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{fullName}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>

              <div className="pt-1">
                <Link
                  to="/settings"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center gap-2 w-full rounded-lg px-2 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full rounded-lg px-2 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close menus */}
      {(showUserMenu || showNotifications) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
        />
      )}
    </header>
  );
}
